import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "../utilities/axios";

const initialState = {
  // Remote requests data
  myRemoteRequests: [],
  allRemoteRequests: [],
  pendingRemoteRequests: [],
  remoteRequestStatistics: null,
  remoteDaysBalance: null,
  
  // Paginated remote requests data
  paginatedRemoteRequests: [],
  paginatedRemoteRequestsStatus: "idle",
  paginatedRemoteRequestsError: null,
  remoteRequestsPagination: {
    page: 1,
    limit: 10,
    total: 0,
    pages: 0
  },
  
  // Status flags for my requests
  getMyRemoteRequestsStatus: "idle",
  getMyRemoteRequestsError: null,
  
  // Status flags for all requests
  getAllRemoteRequestsStatus: "idle",
  getAllRemoteRequestsError: null,
  
  // Status flags for pending requests
  getPendingRemoteRequestsStatus: "idle",
  getPendingRemoteRequestsError: null,
  
  // Status flags for balance
  getRemoteDaysBalanceStatus: "idle",
  getRemoteDaysBalanceError: null,
  
  // Status flags for statistics
  getRemoteRequestStatisticsStatus: "idle",
  getRemoteRequestStatisticsError: null,
  
  // Status flags for create
  createRemoteRequestStatus: "idle",
  createRemoteRequestError: null,
  
  // Status flags for approve
  approveRemoteRequestStatus: "idle",
  approveRemoteRequestError: null,
  
  // Status flags for reject
  rejectRemoteRequestStatus: "idle",
  rejectRemoteRequestError: null,
  
  // Status flags for delete
  deleteRemoteRequestStatus: "idle",
  deleteRemoteRequestError: null,
  
  // Status flags for edit
  editRemoteRequestStatus: "idle",
  editRemoteRequestError: null,
  
  // Status flags for reset
  resetAllRemoteDaysStatus: "idle",
  resetAllRemoteDaysError: null,
};

// Async thunks for API calls
export const getMyRemoteRequests = createAsyncThunk(
  "remoteRequest/getMyRemoteRequests",
  async (_, thunkAPI) => {
    try {
      const response = await axios.get("/remote-requests/my-requests");
      return response.data;
    } catch (error) {
      return thunkAPI.rejectWithValue(error.message || "Failed to fetch remote requests");
    }
  }
);

export const getAllRemoteRequests = createAsyncThunk(
  "remoteRequest/getAllRemoteRequests",
  async (_, thunkAPI) => {
    try {
      const response = await axios.get("/remote-requests");
      return response.data;
    } catch (error) {
      return thunkAPI.rejectWithValue(error.message || "Failed to fetch all remote requests");
    }
  }
);

export const fetchPaginatedRemoteRequests = createAsyncThunk(
  "remoteRequest/fetchPaginatedRemoteRequests",
  async (params, { rejectWithValue }) => {
    try {
      const queryParams = new URLSearchParams();
      if (params.page) queryParams.append("page", params.page);
      if (params.limit) queryParams.append("limit", params.limit);
      if (params.search) queryParams.append("search", params.search);
      if (params.status) queryParams.append("status", params.status);
      if (params.department) queryParams.append("department", params.department);
      if (params.dateStartFrom) queryParams.append("dateStartFrom", params.dateStartFrom);
      if (params.dateStartTo) queryParams.append("dateStartTo", params.dateStartTo);
      if (params.dateEndFrom) queryParams.append("dateEndFrom", params.dateEndFrom);
      if (params.dateEndTo) queryParams.append("dateEndTo", params.dateEndTo);
      if (params.userId) queryParams.append("userId", params.userId);
      if (params.headquarterId) queryParams.append("headquarterId", params.headquarterId);

      const response = await axios.get(`/remote-requests/paginated?${queryParams.toString()}`);
      
      if (response.status === 200) {
        return {
          data: response.data.data || [],
          pagination: response.data.pagination || { page: 1, limit: 10, total: 0, pages: 0 }
        };
      }
      throw new Error(response.statusText);
    } catch (err) {
      return rejectWithValue(
        err.response?.data?.message || err.message || "Failed to fetch remote requests"
      );
    }
  }
);

export const getPendingRemoteRequests = createAsyncThunk(
  "remoteRequest/getPendingRemoteRequests",
  async (_, thunkAPI) => {
    try {
      const response = await axios.get("/remote-requests/pending");
      return response.data;
    } catch (error) {
      return thunkAPI.rejectWithValue(error.message || "Failed to fetch pending remote requests");
    }
  }
);

export const getRemoteDaysBalance = createAsyncThunk(
  "remoteRequest/getRemoteDaysBalance",
  async (_, thunkAPI) => {
    try {
      const response = await axios.get("/remote-requests/balance");
      return response.data;
    } catch (error) {
      return thunkAPI.rejectWithValue(error.message || "Failed to fetch remote days balance");
    }
  }
);

export const getRemoteRequestStatistics = createAsyncThunk(
  "remoteRequest/getRemoteRequestStatistics",
  async (_, thunkAPI) => {
    try {
      const response = await axios.get("/remote-requests/statistics");
      return response.data;
    } catch (error) {
      return thunkAPI.rejectWithValue(error.message || "Failed to fetch remote request statistics");
    }
  }
);

export const createRemoteRequest = createAsyncThunk(
  "remoteRequest/createRemoteRequest",
  async (data, thunkAPI) => {
    try {
      const response = await axios.post("/remote-requests", data);
      return response.data;
    } catch (error) {
      return Promise.reject(
        error.error
          ? error.error
          : error.message
          ? error.message
          : "Failed to create remote request"
      );
    }
  }
);

export const approveRemoteRequest = createAsyncThunk(
  "remoteRequest/approveRemoteRequest",
  async (requestId, thunkAPI) => {
    try {
      const response = await axios.post(`/remote-requests/${requestId}/approve`);
      return response.data;
    } catch (error) {
      const errorMessage = error.response?.data?.error || error.response?.data?.message || error.message || "Failed to approve remote request";
      return thunkAPI.rejectWithValue(errorMessage);
    }
  }
);

export const rejectRemoteRequest = createAsyncThunk(
  "remoteRequest/rejectRemoteRequest",
  async ({ requestId, reason }, thunkAPI) => {
    try {
      const response = await axios.post(`/remote-requests/${requestId}/reject`, { reason });
      return response.data;
    } catch (error) {
      const errorMessage = error.response?.data?.error || error.response?.data?.message || error.message || "Failed to reject remote request";
      return thunkAPI.rejectWithValue(errorMessage);
    }
  }
);

export const deleteRemoteRequest = createAsyncThunk(
  "remoteRequest/deleteRemoteRequest",
  async (requestId, thunkAPI) => {
    try {
      const response = await axios.delete(`/remote-requests/${requestId}`);
      return response.data;
    } catch (error) {
      const errorMessage = error.response?.data?.error || error.response?.data?.message || error.message || "Failed to delete remote request";
      return thunkAPI.rejectWithValue(errorMessage);
    }
  }
);

export const editRemoteRequestCurrentUser = createAsyncThunk(
  "remoteRequest/editRemoteRequestCurrentUser",
  async (body, thunkAPI) => {
    let data;
    const { startDate, endDate, reason, numberOfDays, comment } = body.data;
    try {
      const response = await axios.patch(`/remote-requests/edit/${body.id}`, {
        startDate,
        endDate,
        reason,
        numberOfDays,
        comment,
      });
      data = await response.data;
      if (response.status === 200) {
        thunkAPI.dispatch(getMyRemoteRequests());
        return {
          data: data,
          message: "Your remote request has been updated successfully.",
        };
      }
      throw new Error(response.statusText);
    } catch (err) {
      return Promise.reject(
        err.response?.data?.error || err.message || "Oups !Something went wrong! please try again"
      );
    }
  }
);

export const resetAllRemoteDays = createAsyncThunk(
  "remoteRequest/resetAllRemoteDays",
  async (_, thunkAPI) => {
    try {
      const response = await axios.post("/remote-days/reset");
      return response.data;
    } catch (error) {
      const errorMessage = error.response?.data?.error || error.response?.data?.message || error.message || "Failed to reset remote days";
      return thunkAPI.rejectWithValue(errorMessage);
    }
  }
);

const remoteRequestSlice = createSlice({
  name: "remoteRequest",
  initialState,
  reducers: {
    clearRemoteRequestErrors: (state) => {
      state.createRemoteRequestError = null;
      state.approveRemoteRequestError = null;
      state.rejectRemoteRequestError = null;
      state.deleteRemoteRequestError = null;
    },
    clearRemoteRequestStatus: (state) => {
      state.createRemoteRequestStatus = "idle";
      state.approveRemoteRequestStatus = "idle";
      state.rejectRemoteRequestStatus = "idle";
      state.deleteRemoteRequestStatus = "idle";
    },
  },
  extraReducers: (builder) => {
    builder
      // Get my remote requests
      .addCase(getMyRemoteRequests.pending, (state) => {
        state.getMyRemoteRequestsStatus = "loading";
      })
      .addCase(getMyRemoteRequests.fulfilled, (state, action) => {
        state.getMyRemoteRequestsStatus = "succeeded";
        state.myRemoteRequests = action.payload.data || [];
      })
      .addCase(getMyRemoteRequests.rejected, (state, action) => {
        state.getMyRemoteRequestsStatus = "failed";
        state.getMyRemoteRequestsError = action.payload;
      })
      
      // Get all remote requests
      .addCase(getAllRemoteRequests.pending, (state) => {
        state.getAllRemoteRequestsStatus = "loading";
      })
      .addCase(getAllRemoteRequests.fulfilled, (state, action) => {
        state.getAllRemoteRequestsStatus = "succeeded";
        state.allRemoteRequests = action.payload.data;
      })
      .addCase(getAllRemoteRequests.rejected, (state, action) => {
        state.getAllRemoteRequestsStatus = "failed";
        state.getAllRemoteRequestsError = action.payload;
      })
      
      // Fetch paginated remote requests
      .addCase(fetchPaginatedRemoteRequests.pending, (state) => {
        state.paginatedRemoteRequestsStatus = "loading";
        state.paginatedRemoteRequestsError = null;
      })
      .addCase(fetchPaginatedRemoteRequests.fulfilled, (state, action) => {
        state.paginatedRemoteRequestsStatus = "succeeded";
        state.paginatedRemoteRequests = action.payload.data;
        state.remoteRequestsPagination = action.payload.pagination;
        state.paginatedRemoteRequestsError = null;
      })
      .addCase(fetchPaginatedRemoteRequests.rejected, (state, action) => {
        state.paginatedRemoteRequestsStatus = "failed";
        state.paginatedRemoteRequestsError = action.payload;
      })
      
      // Get pending remote requests
      .addCase(getPendingRemoteRequests.pending, (state) => {
        state.getPendingRemoteRequestsStatus = "loading";
      })
      .addCase(getPendingRemoteRequests.fulfilled, (state, action) => {
        state.getPendingRemoteRequestsStatus = "succeeded";
        state.pendingRemoteRequests = action.payload.data || [];
      })
      .addCase(getPendingRemoteRequests.rejected, (state, action) => {
        state.getPendingRemoteRequestsStatus = "failed";
        state.getPendingRemoteRequestsError = action.payload;
      })
      
      // Get remote days balance
      .addCase(getRemoteDaysBalance.pending, (state) => {
        state.getRemoteDaysBalanceStatus = "loading";
      })
      .addCase(getRemoteDaysBalance.fulfilled, (state, action) => {
        state.getRemoteDaysBalanceStatus = "succeeded";
        state.remoteDaysBalance = action.payload.data;
      })
      .addCase(getRemoteDaysBalance.rejected, (state, action) => {
        state.getRemoteDaysBalanceStatus = "failed";
        state.getRemoteDaysBalanceError = action.payload;
      })
      
      // Get remote request statistics
      .addCase(getRemoteRequestStatistics.pending, (state) => {
        state.getRemoteRequestStatisticsStatus = "loading";
      })
      .addCase(getRemoteRequestStatistics.fulfilled, (state, action) => {
        state.getRemoteRequestStatisticsStatus = "succeeded";
        state.remoteRequestStatistics = action.payload.data;
      })
      .addCase(getRemoteRequestStatistics.rejected, (state, action) => {
        state.getRemoteRequestStatisticsStatus = "failed";
        state.getRemoteRequestStatisticsError = action.payload;
      })
      
      // Create remote request
      .addCase(createRemoteRequest.pending, (state) => {
        state.createRemoteRequestStatus = "loading";
      })
      .addCase(createRemoteRequest.fulfilled, (state, action) => {
        state.createRemoteRequestStatus = "succeeded";
        const newRequest = action.payload.data;
        // Add to my requests
        state.myRemoteRequests.unshift(newRequest);
        // Also add to all requests for RH/Team Lead users
        state.allRemoteRequests.unshift(newRequest);
      })
      .addCase(createRemoteRequest.rejected, (state, action) => {
        state.createRemoteRequestStatus = "failed";
        state.createRemoteRequestError = action.payload;
      })
      
      // Approve remote request
      .addCase(approveRemoteRequest.pending, (state) => {
        state.approveRemoteRequestStatus = "loading";
      })
      .addCase(approveRemoteRequest.fulfilled, (state, action) => {
        state.approveRemoteRequestStatus = "succeeded";
        const updatedRequest = action.payload.data;
        // Update in my requests
        const myRequestIndex = state.myRemoteRequests.findIndex(req => req._id === updatedRequest._id);
        if (myRequestIndex !== -1) {
          state.myRemoteRequests[myRequestIndex] = updatedRequest;
        }
        // Update in pending requests
        const pendingRequestIndex = state.pendingRemoteRequests.findIndex(req => req._id === updatedRequest._id);
        if (pendingRequestIndex !== -1) {
          state.pendingRemoteRequests.splice(pendingRequestIndex, 1);
        }
      })
      .addCase(approveRemoteRequest.rejected, (state, action) => {
        state.approveRemoteRequestStatus = "failed";
        state.approveRemoteRequestError = action.payload;
      })
      
      // Reject remote request
      .addCase(rejectRemoteRequest.pending, (state) => {
        state.rejectRemoteRequestStatus = "loading";
      })
      .addCase(rejectRemoteRequest.fulfilled, (state, action) => {
        state.rejectRemoteRequestStatus = "succeeded";
        const updatedRequest = action.payload.data;
        // Update in my requests
        const myRequestIndex = state.myRemoteRequests.findIndex(req => req._id === updatedRequest._id);
        if (myRequestIndex !== -1) {
          state.myRemoteRequests[myRequestIndex] = updatedRequest;
        }
        // Update in pending requests
        const pendingRequestIndex = state.pendingRemoteRequests.findIndex(req => req._id === updatedRequest._id);
        if (pendingRequestIndex !== -1) {
          state.pendingRemoteRequests.splice(pendingRequestIndex, 1);
        }
      })
      .addCase(rejectRemoteRequest.rejected, (state, action) => {
        state.rejectRemoteRequestStatus = "failed";
        state.rejectRemoteRequestError = action.payload;
      })
      
      // Delete remote request
      .addCase(deleteRemoteRequest.pending, (state) => {
        state.deleteRemoteRequestStatus = "loading";
      })
      .addCase(deleteRemoteRequest.fulfilled, (state, action) => {
        state.deleteRemoteRequestStatus = "succeeded";
        const requestId = action.meta.arg;
        state.myRemoteRequests = state.myRemoteRequests.filter(req => req._id !== requestId);
      })
      .addCase(deleteRemoteRequest.rejected, (state, action) => {
        state.deleteRemoteRequestStatus = "failed";
        state.deleteRemoteRequestError = action.payload;
      })
      
      // Edit remote request
      .addCase(editRemoteRequestCurrentUser.pending, (state) => {
        state.editRemoteRequestStatus = "loading";
      })
      .addCase(editRemoteRequestCurrentUser.fulfilled, (state, action) => {
        state.editRemoteRequestStatus = "succeeded";
        const updatedRequest = action.payload.data.data;
        const requestIndex = state.myRemoteRequests.findIndex(req => req._id === updatedRequest._id);
        if (requestIndex !== -1) {
          state.myRemoteRequests[requestIndex] = updatedRequest;
        }
      })
      .addCase(editRemoteRequestCurrentUser.rejected, (state, action) => {
        state.editRemoteRequestStatus = "failed";
        state.editRemoteRequestError = action.payload;
      });
  },
});

export const { clearRemoteRequestErrors, clearRemoteRequestStatus } = remoteRequestSlice.actions;
export default remoteRequestSlice.reducer;